import type { NewStore, Store, StoreUpdate } from "@hobbyshopify/backend-client";
import { DESCRIPTION_REQUIRED, PRESET_DESCRIPTION } from "./shop-page";

/** The shop profile form, every field a string so an input can bind to it. */
export interface ProfileDraft {
  readonly name: string;
  readonly description: string;
  readonly logoUrl: string;
  readonly currencyCode: string;
}

export type ProfileField = keyof ProfileDraft;
export type ProfileErrors = Partial<Record<ProfileField, string>>;

export const EMPTY_PROFILE: ProfileDraft = { name: "", description: "", logoUrl: "", currencyCode: "USD" };

export function storeToDraft(store?: Store | null): ProfileDraft {
  return {
    name: store?.name ?? "",
    description: store?.description ?? "",
    logoUrl: store?.logoUrl ?? "",
    currencyCode: store?.currencyCode || "USD",
  };
}

/**
 * The form with the description filled in rather than blank: every shop here
 * sells the same thing, so the generic answer is true of all of them. Only the
 * default is preset; clearing it is allowed unless the shop hosts its own page.
 */
export const withPresetDescription = (draft: ProfileDraft): ProfileDraft => ({
  ...draft,
  description: draft.description.trim() || PRESET_DESCRIPTION,
});

/** Trimmed, currency upper-cased: what is validated and what is sent. */
export function normalizeProfileDraft(draft: ProfileDraft): ProfileDraft {
  return {
    name: draft.name.trim(),
    description: draft.description.trim(),
    logoUrl: draft.logoUrl.trim(),
    currencyCode: draft.currencyCode.trim().toUpperCase(),
  };
}

function isUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Mirrors the backend's rules so the merchant is told which field is wrong while
 * looking at it. `requireDescription` is the rule for a shop whose hosted page is
 * its business website: `ShopPageOps.isPublishable` needs a description, so
 * clearing it would silently demote the page Stripe was given.
 */
export function validateProfileDraft(draft: ProfileDraft, o: { requireDescription: boolean }): ProfileErrors {
  const d = normalizeProfileDraft(draft);
  const errors: ProfileErrors = {};
  if (!d.name) errors.name = "Store name is required.";
  else if (d.name.length > 120) errors.name = "Store name is too long.";
  if (o.requireDescription && !d.description) errors.description = DESCRIPTION_REQUIRED;
  if (d.logoUrl && !isUrl(d.logoUrl)) errors.logoUrl = "Enter a valid logo URL.";
  if (!/^[A-Z]{3}$/.test(d.currencyCode)) {
    errors.currencyCode = "Use a three-letter uppercase currency code, for example USD.";
  }
  return errors;
}

/**
 * Blank optional fields leave the form as `undefined` (create) or `null`
 * (update), never as "": the backend's `@IsOptional()` only skips null and
 * undefined, and an empty string would still reach `@IsUrl()`.
 */
export function draftToRequest(draft: ProfileDraft, editing: false): NewStore;
export function draftToRequest(draft: ProfileDraft, editing: true): StoreUpdate;
export function draftToRequest(draft: ProfileDraft, editing: boolean): NewStore | StoreUpdate;
export function draftToRequest(draft: ProfileDraft, editing: boolean): NewStore | StoreUpdate {
  const d = normalizeProfileDraft(draft);
  const blank = editing ? null : undefined;
  const request = {
    name: d.name,
    description: d.description || blank,
    logoUrl: d.logoUrl || blank,
    currencyCode: d.currencyCode || blank,
  };
  return editing ? (request as StoreUpdate) : (request as NewStore);
}
